import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Text } from 'components/styles/Typography';
import useTranslation from '@hooks/useTranslation';

const SizeForm = ({
    variant,
    setShowWarning,
    setVariantQuantity,
    isProdRequest,
    setShowError,
    isButtonClicked,
}) => {
    const [quantity, setQuantity] = useState(0);
    const { locale } = useTranslation();

    const onQuantityChange = (e) => {
        const value = Number(e.target.value);
        if (value <= variant.pack_count) {
            setQuantity(value);
            setVariantQuantity((prev) => {
                if (value === 0) {
                    if (prev.length > 0) {
                        const newPrev = prev.filter(
                            (item) => item.productId !== variant.id,
                        );
                        return newPrev;
                    }
                }
                if (prev.length > 0) {
                    const index = prev.findIndex(
                        (item) => item.productId === variant.id,
                    );
                    if (index > -1) {
                        prev.splice(index, 1);
                    }
                }
                const newPrev = [
                    ...prev,
                    {
                        productId: variant.id,
                        quantity: value,
                        overwrite: true,
                        pre_order: false,
                    },
                ];

                return newPrev;
            });
            setShowError(false);
        } else {
            setQuantity(variant.pack_count);
            setVariantQuantity((prev) => {
                if (prev.length > 0) {
                    const index = prev.findIndex(
                        (item) => item.productId === variant.id,
                    );
                    if (index > -1) {
                        prev.splice(index, 1);
                    }
                }
                const newPrev = [
                    ...prev,
                    {
                        productId: variant.id,
                        quantity: variant.pack_count,
                        overwrite: true,
                        pre_order: false,
                    },
                ];

                return newPrev;
            });
            setShowError(variant.pack_count);
        }
    };

    const onQuantityChangeProdRequest = (e) => {
        const value = Number(e.target.value);

        setQuantity(value);
        if (value < 10) {
            setShowWarning(
                'You must purchase at least 10 packages for this product to be manufactured and shipped to you. For more, you can increase your order quantity in triplicate.',
            );
            setVariantQuantity((prev) => {
                if (prev.length > 0) {
                    const newPrev = prev.filter(
                        (item) => item.productId !== variant.id,
                    );
                    return newPrev;
                }
                return prev;
            });
        } else {
            setVariantQuantity((prev) => {
                if (value === 0) {
                    if (prev.length > 0) {
                        const newPrev = prev.filter(
                            (item) => item.productId !== variant.id,
                        );
                        return newPrev;
                    }
                }
                if (prev.length > 0) {
                    const index = prev.findIndex(
                        (item) => item.productId === variant.id,
                    );

                    if (index > -1) {
                        prev.splice(index, 1);
                    }
                }
                const newPrev = [
                    ...prev,
                    {
                        productId: variant.id,
                        quantity: value,
                        overwrite: true,
                        pre_order: true,
                    },
                ];

                return newPrev;
            });
            setShowWarning(false);
        }
    };

    useEffect(() => {
        if (isButtonClicked) {
            setQuantity(0);
        }
    }, [isButtonClicked]);

    return (
        <Wrapper>
            <img
                src={variant.images[0].urls.original}
                height="74px"
                width="54px"
                alt="variant"
            />

            <Color>
                <Text
                    size="14px"
                    lineHeight="22px"
                    letterSpacing="1px"
                    textTransform="capitalize"
                >
                    {variant?.colour.name[locale]}
                </Text>
            </Color>

            <OptionContainer>
                <OptionWrapper>
                    <Text
                        size="14px"
                        lineHeight="22px"
                        letterSpacing="1px"
                        margin="0"
                    >
                        Pack
                    </Text>
                    {variant?.stock > 0 || isProdRequest ? (
                        <Option
                            value={quantity || ''}
                            max={variant.stock}
                            onChange={
                                isProdRequest
                                    ? onQuantityChangeProdRequest
                                    : onQuantityChange
                            }
                            bold
                        />
                    ) : (
                        <Option disabled />
                    )}
                </OptionWrapper>
                {variant.sizes.map((size) => (
                    <OptionWrapper key={size.size}>
                        <Text
                            size="14px"
                            lineHeight="22px"
                            letterSpacing="1px"
                            margin="0"
                        >
                            {size.size_label}
                        </Text>
                        <Label>{quantity || ''}</Label>
                        <Option disabled />
                    </OptionWrapper>
                ))}
                <OptionWrapper>
                    <Text
                        size="14px"
                        lineHeight="22px"
                        letterSpacing="1px"
                        margin="0"
                    >
                        Units
                    </Text>
                    <Label bold>{quantity * 3}</Label>
                    <Option disabled />
                </OptionWrapper>
            </OptionContainer>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    img {
        margin-left: 7px;
    }
    &::-webkit-scrollbar {
        display: none;
    }
    @media (max-width: 1024px) {
        margin-left: 0;
    }
`;
const Color = styled.div`
    margin-left: 16px;
    width: 78px;
    min-width: 78px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    @media (max-width: 1024px) {
        width: 50px;
        min-width: 50px;
        margin-left: 10px;
    }
`;
const OptionContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 31px;
    width: 344px;
    margin-left: 37px;
    @media (max-width: 768px) {
        width: unset;
        gap: 5px;
        margin-left: 5px;
    }
`;
const OptionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    p {
        position: absolute;
        top: -30px;
    }
`;
const Label = styled.span`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-family: ${(props) =>
        props.bold
            ? 'Futura Now Headline Medium'
            : 'Futura Now Headline Regular'},
        sans-serif;
    font-size: ${(props) => (props.bold ? '16px' : '18px')};
    line-height: ${(props) => (props.bold ? '24px' : '26px')};
    letter-spacing: 1px;
    cursor: not-allowed;
`;
const Option = styled.input`
    width: 44px;
    height: 44px;
    border: 1px solid #cacaca;
    background: transparent;
    cursor: pointer;
    text-align: center;
    font-family: ${(props) => props.bold && 'Futura Now Headline Medium'};
    font-size: 18px !important;
    line-height: 26px;
    letter-spacing: 1px;
    &:disabled {
        background: #e4e4e4;
        cursor: not-allowed;
    }
    @media (max-width: 1024px) {
        width: 40px;
        height: 40px;
    }
`;
export default SizeForm;
